import React, { useState, useEffect } from "react";
import { Table, Button, Form, Space, Typography, Modal } from "antd";
import { useIntl, FormattedMessage } from "react-intl";
import { SearchOutlined } from "@ant-design/icons";

import { deleteConfirmMessage } from "../forms/formsFeedback";
import RestrictedComponent from "../util/RestrictedComponent";
import EditableCell from "./EditableCell";
import TableHeader from "./TableHeader";
import SearchInput from "./SearchInput";
import SystemTable from "../templates/SystemTable";
const { Title } = Typography;

const EditableTable = (props) => {
  const {
    data,
    saveRecord,
    onCancel,
    deleteRecord,
    loading,
    nonEditable,
    built_in_non_editable,
    initialSorter,
    fields,
    title,
    hidePrimary,
    tableHeight,
    tableSize,
    onTableChange,
    current,
    pageSize,
    order,
    field,
    freeText,
    onTextChange,
    total,
    resource,
    showInput,
    sub_table_props,
    source,
  } = props;

  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [columns, setColumns] = useState([]);
  const [editingKey, setEditingKey] = useState("");
  const [sortOrder, setSortOrder] = useState("ascend");
  const [sortField, setSortField] = useState(initialSorter);
  const [isSubTableModalVisible, setIsSubTableModalVisible] = useState(false);
  const [subTableSource, setSubTableSource] = useState("");
  const [subTableIdentifier, setSubTableIdentifier] = useState("");
  const [modal, contextHolder] = Modal.useModal();
  const intl = useIntl();

  useEffect(() => {
    const columnsArr = getColumns(fields);
    columnsArr.push(getActionsColumn());
    setColumns(columnsArr);
  }, [editingKey, sortOrder, sortField, order, field]);

  useEffect(() => {
    setDataSource(data);
  }, [data]);

  const isEditing = (record) => record.id === editingKey;

  const showSubTableModal = (record) => {
    setSubTableSource(`${source}/${record.id}/${sub_table_props?.source}`);
    setSubTableIdentifier(record.id);
    setIsSubTableModalVisible(true);
  };

  const handleSubTableModalClose = () => {
    setIsSubTableModalVisible(false);
  };

  const edit = (record) => {
    const converted = Object.fromEntries(
      Object.entries(record).map(([key, value]) => [
        key,
        typeof value === "string" && value !== undefined && value !== ""
          ? intl.formatMessage({
              id: `app.settings.system_tables.${value}`,
              defaultMessage: value.toString(),
            })
          : value,
      ])
    );
    form.setFieldsValue(converted);
    setEditingKey(record.id);
  };

  const cancel = (record) => {
    setEditingKey("");
    onCancel(record);
  };

  const handleAdd = () => {
    const newData = fields.reduce(
      (acc, item) => ({ ...acc, [item.key]: undefined }),
      {}
    );
    const newId =
      Array.isArray(dataSource) && dataSource.length > 0
        ? Math.max(...dataSource.map((item) => item.id || 0)) + 1
        : 1;
    newData.id = newId;
    setDataSource((prevDataSource) => [...(prevDataSource || []), newData]);
    edit(newData);
  };

  const save = async (key) => {
    try {
      const row = await form.validateFields();
      const newData = [...data];
      const index = newData.findIndex((item) => key === item.id);

      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, { ...item, ...row });
        setDataSource(newData);
        saveRecord({ ...item, ...row });
        setEditingKey("");
      } else {
        newData.push(row);
        setDataSource(newData);
        saveRecord({ ...row });
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const handleDelete = (record) => {
    deleteConfirmMessage(
      intl,
      intl.formatMessage({
        id: `app.settings.system_tables.${record.name}`,
        defaultMessage: record.name,
      }),
      "system_tables",
      record,
      onConfirm,
      modal
    );
  };

  const onConfirm = (record) => {
    deleteRecord(record);
  };

  const getColumns = (fields) => {
    return fields.map((item) => ({
      ...item,
      title: intl.formatMessage({
        id: `app.settings.system_tables.${item.title || item.key}`,
        defaultMessage: item.key,
      }),
      dataIndex: item.key,
      editable: !nonEditable || !nonEditable.includes(item.key),
      ...(item.sorter && {
        sortDirections: ["ascend", "descend", "ascend"],
        sortOrder: order && item.key === field ? order : null,
        sortIndex: item.key,
        sorter: true,
      }),
    }));
  };

  const getActionsColumn = () => ({
    title: intl.formatMessage({ id: `app.settings.system_tables.actions` }),
    render: (_, record) => {
      const editable = isEditing(record);
      return record.nonEditAble ? null : editable ? (
        <Space size="middle">
          <Button type="text" onClick={() => save(record.id)}>
            <FormattedMessage id={"app.actions.save"} />
          </Button>
          <Button danger type="text" onClick={() => cancel(record)}>
            <FormattedMessage id={"app.actions.cancel"} />
          </Button>
        </Space>
      ) : (
        <RestrictedComponent
          resource={resource || "organization.settings"}
          action={"editor"}
          comp={null}
        >
          <Space size="middle">
            {!record.read_only && (
              <Button
                type="text"
                disabled={editingKey !== ""}
                onClick={() => edit(record)}
              >
                <FormattedMessage id={"app.actions.edit"} />
              </Button>
            )}
            {sub_table_props?.source && !record.read_only && (
              <Button
                type="text"
                disabled={editingKey !== ""}
                onClick={() => showSubTableModal(record)}
              >
                <FormattedMessage id={"app.actions.view_sub_table"} />
              </Button>
            )}
            {!record.built_in && (
              <Button
                disabled={editingKey !== ""}
                danger
                type="text"
                onClick={() => handleDelete(record)}
              >
                <FormattedMessage id={"app.actions.delete"} />
              </Button>
            )}
          </Space>
        </RestrictedComponent>
      );
    },
  });

  const components = {
    body: {
      cell: EditableCell,
    },
  };

  const finalColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        ...col,
        built_in_non_editable,
        disabledFields: record.disabledFields,
        requiredFields: record.requiredFields,
      }),
    };
  });

  const onChange = (pagination, filters, sorter, extra) => {
    onTableChange({ ...pagination, filters, ...sorter, extra });
    setSortOrder(sorter.order);
    setSortField(sorter.field);
  };

  const pagination = {
    current,
    pageSize: editingKey === "" ? pageSize : pageSize + 1,
    total,
    pageSizeOptions: [10, 50, 100],
  };

  return (
    <div>
      {contextHolder}
      <TableHeader
        title={title}
        hidePrimary={hidePrimary}
        resource={resource}
        handleAdd={handleAdd}
        editingKey={editingKey}
      />
      <Space size="middle" direction={"vertical"} style={{ width: "100%" }}>
        {showInput && (
          <SearchInput
            freeText={freeText}
            onTextChange={onTextChange}
            intl={intl}
          />
        )}
        <Form form={form} component={false}>
          <Table
            bordered
            components={components}
            rowKey={(record) => record.id}
            scroll={{
              y:
                tableHeight ||
                (showInput
                  ? window.innerHeight - 536
                  : window.innerHeight - 496),
            }}
            size={tableSize || "default"}
            rowClassName={() => "editable-row"}
            dataSource={dataSource}
            columns={finalColumns}
            loading={loading}
            pagination={pagination}
            onChange={onChange}
          />
        </Form>
      </Space>
      <Modal
        title={intl.formatMessage({
          id: `app.settings.system_tables.${sub_table_props?.source}`,
        })}
        open={isSubTableModalVisible}
        onCancel={handleSubTableModalClose}
        footer={null}
        width={800}
      >
        <SystemTable
          {...sub_table_props}
          source={subTableSource}
          subTableIdentifier={subTableIdentifier}
          subTable
        />
      </Modal>
    </div>
  );
};

export default EditableTable;
